import { useCallback, useEffect, useMemo, useRef } from "react";

import { defaultSYS4120_R01Filter, SYS4120_R01Filter, useSYS4120_R01Store } from "./Store_SYS4120_R01";
import useUiuxStore from "src/store/useUiuxStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { validateString } from "src/utils/string/stringUtil";
import { formatLocalDate } from "src/utils/date/dateUtils";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { createSYS4120_R01, deleteSYS4120_R01, downloadExcel_SYS4120_R01, getSYS4120_R01AllCount, getSYS4120_R01List, getSYS4120_R01One, patchSYS4120_R01 } from "./Api_SYS4120_R01";
import { defaultSYS4120_R01Dto, SYS4120_R01Dto } from "./Dto_SYS4120_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { defaultItem, ItemDto } from "src/dtos/item/itemDto";
import { validateCustomCd } from "src/utils/grid/GridValidateContent";

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS4120_R01Dto> | null>;
	containerRef: React.RefObject<HTMLDivElement | null>;
	
}

export const Content_SYS4120_R01 = (props: Props) => {

	const {
		gridRef,
		containerRef,
		
	} = props;

	useFetchActionbarAuth("SYS4120_R01");
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	
	const { 
		
		SYS4120_R01List, setSYS4120_R01List, 
		selectedSYS4120_R01,  setSelectedSYS4120_R01, 
		editSYS4120_R01, setEditSYS4120_R01, 
		allCount, setAllCount,
		initData,
		setScrollTop,
		} = useSYS4120_R01Store();

	const { 
		SYS4120_R01Filter, setSYS4120_R01Filter,
		SYS4120_R01PastFilter, setSYS4120_R01PastFilter,
	 } = useSYS4120_R01Store();
	const { setMessage } = useUiuxStore();
	const { employeeCdList } = useCommonCodeStore();

	const isResizing = useRef(true);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_SYS4120_R01(
			'',
			getParams(SYS4120_R01Filter),
			SYS4120_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '공지사항 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [SYS4120_R01Filter, SYS4120_R01List, setLoadingSpinner]);

	const getParams = (filter: Partial<SYS4120_R01Filter>) => {
		return {
			startDate: filter.dateRange?.from ? formatLocalDate(filter.dateRange.from) : undefined,
			endDate: filter.dateRange?.to ? formatLocalDate(filter.dateRange.to) : undefined,
			topNotice: filter.topNotice,
		}
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getSYS4120_R01AllCount(getParams(SYS4120_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [SYS4120_R01Filter]);

	const defaultSYS4120_R01Row = useCallback(() => {
		return {
			...defaultSYS4120_R01Dto,
			inserted: true,
		}
	}, [defaultSYS4120_R01Dto]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const response = await getSYS4120_R01List(
			'search',
			getParams(SYS4120_R01Filter),
			SYS4120_R01List.length ?? 0
		);
		let newList = [...response.data.data ] as SYS4120_R01Dto[];
		setSYS4120_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedSYS4120_R01,
				() => true,// () => !newList.find(value => value.itemCd === selectedSYS4120_R01?.itemCd)
			);
			setSYS4120_R01PastFilter(SYS4120_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			handleCreateClick();
		}
		setLoadingSpinner(false);

		return response.data.data;
	}, [SYS4120_R01Filter, SYS4120_R01List, gridRef, editSYS4120_R01, setSYS4120_R01List, setSelectedSYS4120_R01, setScrollTop]);

	const fetchOne = useCallback(async () => {
		if (!selectedSYS4120_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getSYS4120_R01One(selectedSYS4120_R01.noticeNo);
			setEditSYS4120_R01({ ...defaultSYS4120_R01Row, ...response.data.data });
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [selectedSYS4120_R01]);

	const initContainerData = useCallback(async () => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	const handleCreateClick = useCallback(async () => {
		initNoticeData();
	}, []);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedSYS4120_R01) {
			showWarning('선택된 공지사항이 없습니다.'); return;
		}
		if (window.confirm('정말로 삭제하시겠습니까?')) {
			await deleteSYS4120_R01(selectedSYS4120_R01.noticeNo);
			refetchUpdateList('delete', selectedSYS4120_R01, selectedSYS4120_R01, 'noticeNo', SYS4120_R01List, setSYS4120_R01List);
			handleCreateClick();
			showSuccess(`${selectedSYS4120_R01.noticeNo} 공지사항 삭제가 완료되었습니다.`)
		}

	}, [selectedSYS4120_R01]);

	const validateInsertData = (data: SYS4120_R01Dto) => {
		
		if (!validateString(data.noticeTitle)) {
			showWarning("제목을 입력하세요."); return false;
		}
		if (!validateString(data.noticeComment)) {
			showWarning("내용을 입력하세요."); return false;
		}
		if (!validateString(data.writerEmpNo) && !validateString(data.writerEmpNm)) {
			showWarning("게시자를 선택하세요."); return false;
		}
		if (validateString(data.writerEmpNo)) {
			const employeeCdInfo = employeeCdList.find(value => value.empNo === data.writerEmpNo);
			if (!employeeCdInfo) {
				showWarning("게시자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)"); return false;
			}
		}
		if (!data.noticeFromDt) {
			showWarning("게시 시작일을 선택하세요."); return false;
		}
		if (!data.noticeEndDt) {
			showWarning("게시 종료일을 선택하세요."); return false;
		}
		if (!validateString(data.noticeComment)) {
			showWarning("내용을 입력하세요."); return false;
		}
		return true;
	}

	const handleSaveClick = useCallback(async () => {

		if (!editSYS4120_R01) {
			showWarning("공지사항을 선택하세요."); return;
		}
		if (!validateInsertData(editSYS4120_R01)) return;

		let processName = '';
		let responseData: SYS4120_R01Dto = { ...defaultSYS4120_R01Dto };
	
		if (selectedSYS4120_R01) {
			processName = 'patch';
			const response = await patchSYS4120_R01(editSYS4120_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editSYS4120_R01.noticeNo} 공지사항 수정이 완료되었습니다.`);
					setMessage(`${editSYS4120_R01.noticeNo} 공지사항 수정이 완료되었습니다.`);
				}else return;
			}
		} else {
			processName = 'post';
			const response = await createSYS4120_R01(editSYS4120_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editSYS4120_R01.noticeNo} 공지사항 등록이 완료되었습니다.`);
					setMessage(`${editSYS4120_R01.noticeNo} 공지사항 등록이 완료되었습니다.`);
				}else return;
			}
		}
	
		setTimeout(() => {
			setMessage("");
		}, 5000)
		if(editSYS4120_R01) {
			refetchUpdateList(processName, responseData, editSYS4120_R01, 'noticeNo', SYS4120_R01List, setSYS4120_R01List);
			setSelectedSYS4120_R01(responseData);
		}
		
	}, [editSYS4120_R01, selectedSYS4120_R01]);
	
	const initNoticeData = () => {
		setSelectedSYS4120_R01(undefined);
		setEditSYS4120_R01(defaultSYS4120_R01Row());	
	}

	//selectedSYS4120_R01 변경 시 공지사항 이미지 불러오기
	useEffect(() => {
		if (selectedSYS4120_R01) {
			fetchOne();
		}else{
			setEditSYS4120_R01(defaultSYS4120_R01Row());
		}
	}, [selectedSYS4120_R01]);

	return {
		handleExcelClick,
		defaultSYS4120_R01Row,
		fetchData,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	}
}
